import { queryOptions } from "@tanstack/react-query";
import { apiClient, orgHeader, unwrap } from "../../../api/client";
import type { components } from "../../../api/generated/vision-api";

export type WidgetPage = components["schemas"]["WidgetPage"];
export type LatestResult = components["schemas"]["LatestResult"];
export type Binding = { device: string; datapoint: string };

/** How often the page reads the latest values while the live socket is not signed in (WEB-09). */
export const LATEST_EVERY_MS = 30_000;

export const deviceQuery = (orgId: string, id: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/devices/{device_id}", id],
    queryFn: () =>
      unwrap(
        apiClient("customers").GET("/v1/devices/{device_id}", {
          params: { path: { device_id: id }, header: orgHeader(orgId) },
        }),
      ),
  });

/** The device page document with its texts in the app's language (WEB-F-DEV-03). */
export const devicePageQuery = (orgId: string, id: string, language: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/devices/{device_id}/page", id, language],
    queryFn: () =>
      unwrap(
        apiClient("customers").GET("/v1/devices/{device_id}/page", {
          params: { path: { device_id: id }, header: { "Accept-Language": language, ...orgHeader(orgId) } },
        }),
      ),
    staleTime: 5 * 60_000,
  });

/** POST /v1/latest takes at most 200 bindings per request (vision-api LatestRequest). */
export const LATEST_BATCH = 200;

/** The latest value of every binding on the page, in batches of LATEST_BATCH merged into one result. */
export async function readLatest(bindings: Binding[], orgId = ""): Promise<LatestResult> {
  const batches: Binding[][] = [];
  for (let i = 0; i < bindings.length; i += LATEST_BATCH) batches.push(bindings.slice(i, i + LATEST_BATCH));
  const parts = await Promise.all(
    batches.map((b) =>
      unwrap(
        apiClient("customers").POST("/v1/latest", { params: { header: orgHeader(orgId) }, body: { bindings: b } }),
      ),
    ),
  );
  return parts.reduce<LatestResult>(
    (all, p) => ({
      points: { ...all.points, ...p.points },
      devices: { ...all.devices, ...p.devices },
      refused: [...all.refused, ...p.refused],
    }),
    { points: {}, devices: {}, refused: [] },
  );
}

/** Polls every LATEST_EVERY_MS until the live socket is signed in; after that the socket patches the result. */
export const latestQuery = (orgId: string, bindings: Binding[], live = false) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/latest", bindings.map((b) => `${b.device}/${b.datapoint}`).sort()],
    queryFn: () => readLatest(bindings, orgId),
    enabled: bindings.length > 0,
    refetchInterval: live ? false : LATEST_EVERY_MS,
  });

export interface HistoryParams {
  datapoint: string;
  from: string;
  to: string;
  points?: number;
  aggregate?: "avg" | "min" | "max" | "sum" | "last";
  bucket?: string;
}

/** One datapoint over a range (WEB-F-DEV-04); the server picks the resolution from `points` unless a bucket is set. */
export const historyQuery = (orgId: string, device: string, p: HistoryParams) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/devices/{device_id}/history", device, p],
    queryFn: () =>
      unwrap(
        apiClient("customers").GET("/v1/devices/{device_id}/history", {
          params: { path: { device_id: device }, query: p, header: orgHeader(orgId) },
        }),
      ),
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });

/** The device's active and acknowledged alarms, newest first, for an alarm_list on its page (WFX-02 AlarmItem). */
export const deviceAlarmsQuery = (orgId: string, device: string, enabled: boolean) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/alarms", "device", device],
    queryFn: () =>
      unwrap(
        apiClient("customers").GET("/v1/alarms", {
          params: { query: { scope_kind: "device", scope_id: device }, header: orgHeader(orgId) },
        }),
      ),
    enabled,
    refetchInterval: LATEST_EVERY_MS,
  });

/** How often a shown statistics widget reads its numbers again (pro-statistics PST-33). */
export const STATISTICS_EVERY_MS = 5 * 60_000;

/** A period_totals or period_compare widget's numbers (vision-api getStatisticsPeriods, PST-12 to PST-23). */
export const statisticsPeriodsQuery = (
  orgId: string,
  device: string,
  datapoint: string,
  period?: "week" | "month" | "year",
) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/devices/{device_id}/statistics/periods", device, datapoint, period ?? "all"],
    queryFn: () =>
      unwrap(
        apiClient("customers").GET("/v1/devices/{device_id}/statistics/periods", {
          params: {
            path: { device_id: device },
            query: period ? { datapoint, periods: [period], compare: "last_year" } : { datapoint },
            header: orgHeader(orgId),
          },
        }),
      ),
    staleTime: STATISTICS_EVERY_MS,
    refetchInterval: STATISTICS_EVERY_MS,
    retry: false,
  });

/** A year_compare widget's months of this year and last year (vision-api getStatisticsMonths). */
export const statisticsMonthsQuery = (orgId: string, device: string, datapoint: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/devices/{device_id}/statistics/months", device, datapoint],
    queryFn: () =>
      unwrap(
        apiClient("customers").GET("/v1/devices/{device_id}/statistics/months", {
          params: { path: { device_id: device }, query: { datapoint, years: 2 }, header: orgHeader(orgId) },
        }),
      ),
    staleTime: STATISTICS_EVERY_MS,
    refetchInterval: STATISTICS_EVERY_MS,
    retry: false,
  });

/** A device's reset counters (vision-api listDeviceCounters, weather-station-widgets WSW-10, WSW-22). */
export const countersQuery = (orgId: string, device: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/devices/{device_id}/counters", device],
    queryFn: () =>
      unwrap(
        apiClient("customers").GET("/v1/devices/{device_id}/counters", {
          params: { path: { device_id: device }, header: orgHeader(orgId) },
        }),
      ),
    staleTime: STATISTICS_EVERY_MS,
    refetchInterval: STATISTICS_EVERY_MS,
    retry: false,
  });

/** The periods a counter's resets ended, newest first (vision-api listCounterPeriods, WSW-14). */
export const counterPeriodsQuery = (orgId: string, device: string, datapoint: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/devices/{device_id}/counters/{datapoint}/periods", device, datapoint],
    queryFn: () =>
      unwrap(
        apiClient("customers").GET("/v1/devices/{device_id}/counters/{datapoint}/periods", {
          params: { path: { device_id: device, datapoint }, header: orgHeader(orgId) },
        }),
      ),
  });

/** Resets the counter that starts at `since`; a 409 counter_changed means someone else reset it first (WSW-12). */
export const resetCounter = (orgId: string, device: string, datapoint: string, since: string) =>
  unwrap(
    apiClient("customers").POST("/v1/devices/{device_id}/counters/{datapoint}/reset", {
      params: { path: { device_id: device, datapoint }, header: orgHeader(orgId) },
      body: { since },
    }),
  );
